import React, { useState, useCallback, useRef, useEffect } from 'react';
import '../css/TopBar.css'; 

// Define the shape of a menu item
interface MenuItem {
  label: string;
  onClick: () => void;
  icon?: React.ReactNode; // Optional icon for the menu item
}

// Define the props for the DropdownMenu component
interface DropdownMenuProps {
  buttonContent: React.ReactNode; // Content for the button that toggles the menu (e.g., "Options", an icon)
  items: MenuItem[]; // Array of menu items
  position?: 'left' | 'right'; // Where the dropdown menu should align relative to the button
  className?: string; // Optional additional classes for the main container
}

const DropdownMenu: React.FC<DropdownMenuProps> = ({
  buttonContent,
  items,
  position = 'right', // Default to right alignment
  className
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null); // Ref for the entire dropdown container

  // Function to toggle menu visibility
  const toggleMenu = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle item click and close menu
  const handleItemClick = useCallback((onClickHandler: () => void) => {
    onClickHandler();
    setIsOpen(false); // Close menu after clicking an item
  }, []);

  const menuPositionClass = position === 'left' ? 'right-0' : 'left-0';

  return (
    <div className={`relative inline-block text-left ${className}`} ref={menuRef}>
      <div>
        <button
          type="button"
          className="inline-flex justify-center w-full rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          id="menu-button"
          aria-expanded={isOpen}
          aria-haspopup="true"
          onClick={toggleMenu}
        >
          {buttonContent}
          {/* Dropdown arrow icon */}
          <svg
            className="-mr-1 ml-2 h-5 w-5"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      </div>

      {isOpen && (
        <div
          className={`origin-top-right absolute mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-50 ${menuPositionClass}`}
          role="menu"
          aria-orientation="vertical"
          aria-labelledby="menu-button"
          tabIndex={-1}
        >
          <div className="py-1" role="none">
            {items.map((item, index) => (
              <button
                key={index} // Using index as key is okay if items don't change order or get added/removed frequently
                onClick={() => handleItemClick(item.onClick)}
                className="text-gray-700 block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 hover:text-gray-900 flex items-center space-x-2"
                role="menuitem"
                tabIndex={-1}
              >
                {item.icon && <span className="flex-shrink-0">{item.icon}</span>}
                <span>{item.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};


// Main TopBar component
interface TopBarProps {
  onNavigate: (page: string) => void;
  currentPage: string;
}

const onLogout = () => {
  //window.location.href = "/login"
  onNavigate('Login')
}

const onNavigate = (page: string) => {
  localStorage.setItem('page', page);
  //window.location.replace('/')
  window.location.reload();
}

const TopBar: React.FC<TopBarProps> = ({ currentPage }) => {

  let userName = null;
  let userEmail = null;

  const loggedInUserObj = JSON.parse(localStorage.getItem('loggedInUserObj') as any);
  //console.log("loggedInUserObj=" + JSON.stringify(loggedInUserObj));
  if (loggedInUserObj) {
    userName = loggedInUserObj.fullName;
    userEmail = loggedInUserObj.userName;
  }

  // Define menu items for the dropdown
  const profileMenuItems: MenuItem[] = [
    {
      label: 'Profile',
      onClick: () => {
        // Handle Profile action (e.g., navigate to profile page or open a modal)
        alert('Navigating to profile...');
      },
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
        </svg>
      ),
    },
    {
      label: 'Logout',
      onClick: onLogout, // Use the provided onLogout handler
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 001 1h8a1 1 0 001-1v-2a1 1 0 10-2 0v2H4V4h7a1 1 0 100-2H3zm11.707 5.707a1 1 0 00-1.414-1.414L10 9.586l-2.293-2.293a1 1 0 10-1.414 1.414L8.586 11l-2.293 2.293a1 1 0 001.414 1.414L10 12.414l2.293 2.293a1 1 0 001.414-1.414L11.414 11l2.293-2.293a1 1 0 000-1.414z" clipRule="evenodd" />
        </svg>
      ),
    },
  ];

  return (
    <>
      <div className="topbar-container" > {/* Spacer to prevent content from being hidden behind the fixed TopBar */}

    <header className="bg-white shadow-md rounded-lg p-4 flex flex-col sm:flex-row justify-between items-center fixed top-0 left-0 right-0 z-50">
      <div className="flex items-center space-x-4 mb-4 sm:mb-0">
        {/* Logo or App Name */}
        <div className="flex items-center justify-center   rounded-lg bg-blue-600 text-white font-bold text-lg">
          <img src="/images/RGD_logo.png" alt="Logo" className="h-8 w-8 mr-2" />
        </div>
        <span className="text-xl font-extrabold text-blue-800">Royal Guide Travel</span>
      </div>

      {/* Main Navigation Menu */}
      <nav className="flex-1 flex justify-center space-x-2 sm:space-x-4">
        <button
          onClick={() => onNavigate('Home')}
          className={`text-gray-700 font-semibold text-lg px-4 py-2 rounded-md transition-colors duration-200 hover:bg-gray-100 ${currentPage === 'Home' ? 'text-blue-600 bg-gray-100' : ''}`}
        >
          Home
        </button>
        <button
          onClick={() => onNavigate('Dashboard')}
          className={`text-gray-700 font-semibold text-lg px-4 py-2 rounded-md transition-colors duration-200 hover:bg-gray-100 ${currentPage === 'Dashboard' ? 'text-blue-600 bg-gray-100' : ''}`}
        >
          Dashboard
        </button>
        <button
          onClick={() => onNavigate('Inbox')}
          className={`text-gray-700 font-semibold text-lg px-4 py-2 rounded-md transition-colors duration-200 hover:bg-gray-100 ${currentPage === 'Dashboard' ? 'text-blue-600 bg-gray-100' : ''}`}
        >
          Inbox
        </button>        
        {/* <button
          onClick={onLogout}
          className="text-red-500 font-semibold text-lg px-4 py-2 rounded-md transition-colors duration-200 hover:bg-red-50"
        >
          Logout
        </button> */}
      </nav>

      {/* User Info and Dropdown Menu */}
      <div className="flex items-center space-x-4 mt-4 sm:mt-0">
        {/* <div className="flex-col hidden sm:flex text-right">
          <span className="font-semibold text-gray-800">{userName}</span>
          <span className="text-sm text-gray-500">{userEmail}</span>
        </div> */}
        <DropdownMenu
          buttonContent={
            <div className="flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
              </svg>
              <span className="ml-2 hidden sm:inline">{loggedInUserObj.fullName}</span>
            </div>
          }
          items={profileMenuItems}
          position="right"
          
        />
      </div>
    </header>
          </div>
    </>
  );
};

export default TopBar;


// // Demo App component to show TopBar in action
// const App = () => {
//   const [isLoggedIn, setIsLoggedIn] = useState(false);
//   const [user, setUser] = useState({
//     name: 'Jane Doe',
//     email: 'jane.doe@example.com',
//   });
//   const [currentPage, setCurrentPage] = useState('Home');

//   const handleLogin = () => {
//     setIsLoggedIn(true);
//   };

//   const handleLogout = useCallback(() => {
//     setIsLoggedIn(false);
//     setCurrentPage('Home');
//   }, []);

//   const handleNavigate = useCallback((page: string) => {
//     setCurrentPage(page);
//   }, []);

//   // Conditional content to render based on the current page
//   const renderContent = () => {
//     switch (currentPage) {
//       case 'Home':
//         return (
//           <div className="bg-white p-6 rounded-lg shadow-md">
//             <h1 className="text-3xl font-bold text-gray-900 mb-4">Welcome Home!</h1>
//             <p className="text-gray-600">This is the main home page content.</p>
//           </div>
//         );
//       case 'Dashboard':
//         return (
//           <div className="bg-white p-6 rounded-lg shadow-md">
//             <h1 className="text-3xl font-bold text-gray-900 mb-4">Dashboard Overview</h1>
//             <p className="text-gray-600">Here you would find a summary of your CRM data.</p>
//           </div>
//         );
//       default:
//         return null;
//     }
//   };

//   return (
//     <div className="min-h-screen bg-gray-100 font-sans">
//       {/* Tailwind CSS CDN and font import */}
//       <script src="https://cdn.tailwindcss.com"></script>
//       <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700&display=swap" rel="stylesheet" />
//       <style>{`body { font-family: 'Inter', sans-serif; }`}</style>

//         <>
//           <TopBar
//             userName={user.name}
//             userEmail={user.email}
//             onLogout={handleLogout}
//             onNavigate={handleNavigate}
//             currentPage={currentPage}
//           />
//           <main className="pt-32 p-4 sm:p-6 lg:p-8">
//             {renderContent()}
//           </main>
//         </>

//     </div>
//   );
// };

// export default App;
